import React from 'react';
import { Box, Flex, HStack, Text, Icon, IconButton, Button, Spinner } from '@chakra-ui/react';
import { FiPlay, FiPause, FiMic } from 'react-icons/fi';
import WaveSurfer from 'wavesurfer.js';
import { mmss } from './time';

/**
 * A voice note, the way WhatsApp plays one: play/pause, a scrubbable
 * waveform, the time remaining, and a 1×/1.5×/2× speed toggle.
 *
 * ★ COLOURS ARE READ FROM THE THEME AT RUNTIME. wavesurfer paints a canvas,
 * which cannot resolve a CSS variable — so the two colours it needs are read
 * off `body` (Chakra's `cssVarsRoot`) when the player mounts. No literal hex
 * here, and the waveform still flips with the colour mode on remount.
 *
 * ★ ONE AT A TIME. Starting a note pauses whichever other one is playing, the
 * way every messaging app does it, through a tiny module-level bus.
 */
const bus = new EventTarget();
const RATES = [1, 1.5, 2];

const themeVar = (name) =>
	getComputedStyle(document.body).getPropertyValue(name).trim() || undefined;

export default function VoiceNotePlayer({ url, outbound }) {
	const containerRef = React.useRef(null);
	const wsRef = React.useRef(null);
	const [ready, setReady] = React.useState(false);
	const [error, setError] = React.useState(false);
	const [playing, setPlaying] = React.useState(false);
	const [duration, setDuration] = React.useState(0);
	const [current, setCurrent] = React.useState(0);
	const [rate, setRate] = React.useState(1);

	React.useEffect(() => {
		if (!url || !containerRef.current) return undefined;
		setReady(false);
		setError(false);
		setPlaying(false);
		setCurrent(0);

		const ws = WaveSurfer.create({
			container: containerRef.current,
			url,
			height: 28,
			barWidth: 2.5,
			barGap: 2,
			barRadius: 2,
			cursorWidth: 0,
			normalize: true,
			waveColor: themeVar('--chakra-colors-wa-inkMuted'),
			progressColor: themeVar('--chakra-colors-wa-accent'),
		});
		wsRef.current = ws;

		ws.on('ready', (d) => {
			setDuration(d || ws.getDuration());
			setReady(true);
		});
		ws.on('timeupdate', (t) => setCurrent(t));
		ws.on('play', () => setPlaying(true));
		ws.on('pause', () => setPlaying(false));
		ws.on('finish', () => {
			setPlaying(false);
			setCurrent(0);
		});
		ws.on('error', () => setError(true));

		const onOther = (e) => {
			if (e.detail !== ws && ws.isPlaying()) ws.pause();
		};
		bus.addEventListener('play', onOther);

		return () => {
			bus.removeEventListener('play', onOther);
			try {
				ws.destroy();
			} catch (_) {
				// Already gone — a re-render during teardown is harmless.
			}
			wsRef.current = null;
		};
	}, [url]);

	const toggle = () => {
		const ws = wsRef.current;
		if (!ws || !ready) return;
		if (ws.isPlaying()) {
			ws.pause();
		} else {
			bus.dispatchEvent(new CustomEvent('play', { detail: ws }));
			ws.play();
		}
	};

	const cycleRate = () => {
		const next = RATES[(RATES.indexOf(rate) + 1) % RATES.length];
		setRate(next);
		wsRef.current?.setPlaybackRate(next, true);
	};

	const shown = playing || current > 0 ? Math.max(0, duration - current) : duration;

	return (
		// ★ THE FLOOR GIVES WAY ON A PHONE. 230px plus the bubble padding is wider
		// than the bubble itself at phone width, so the player pushed out of it.
		// The waveform and the timer still read perfectly at 180px.
		<Flex align="center" minW={{ base: '180px', md: '230px' }} maxW="min(300px, 100%)" w="100%" py="1">
			<IconButton
				aria-label={playing ? 'Pause voice message' : 'Play voice message'}
				icon={ready ? <Icon as={playing ? FiPause : FiPlay} boxSize="5" /> : error ? <Icon as={FiMic} /> : <Spinner size="xs" />}
				size="sm"
				variant="ghost"
				borderRadius="full"
				color="wa.inkMuted"
				_hover={{ color: 'wa.ink', bg: 'transparent' }}
				isDisabled={!ready}
				onClick={toggle}
				mr="1"
			/>
			<Box flex="1" minW="0" position="relative">
				<Box ref={containerRef} w="100%" cursor={ready ? 'pointer' : 'default'} />
				{error && (
					<Text fontSize="xs" color="wa.inkMuted" position="absolute" top="1" left="0">
						Audio unavailable
					</Text>
				)}
			</Box>
			<HStack spacing="1.5" ml="2" flexShrink={0}>
				<Text fontSize="xs" color="wa.inkMuted" minW="34px" textAlign="right" fontVariantNumeric="tabular-nums">
					{error ? '--:--' : mmss(shown)}
				</Text>
				{(playing || current > 0) && (
					<Button
						size="xs"
						h="20px"
						px="1.5"
						borderRadius="full"
						bg={outbound ? 'wa.incoming' : 'wa.chip'}
						color="wa.ink"
						fontSize="0.65rem"
						onClick={cycleRate}
						aria-label={`Playback speed ${rate}x`}
					>
						{rate}×
					</Button>
				)}
			</HStack>
		</Flex>
	);
}
